import React, { useContext, useMemo } from 'react'
import classNames from 'classnames'
import _ from 'lodash'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Avatar } from 'browser/components/atomic-elements/atoms/avatar/avatar'
import { HelpBlock } from 'browser/components/atomic-elements/atoms/help-block/help-block'
import { Label } from 'browser/components/atomic-elements/atoms/label/label'
import { InternalViewContext } from 'browser/contexts/internal-view/internal-view-context'
import { Owner } from 'shared-libs/generated/server-types/entity'
import {
  findSchemaLabel,
  formatRelativePathLabel,
  isDateWrapper,
  isEntityRef,
  normalizeEntityPath,
} from 'shared-libs/helpers/utils'
import { formatFeedDateTime, renderAuthorHeader, renderEventTimestamp, renderDeviceInfoTooltipContent } from '../util'
import { EntityPreviewInline } from './entity-preview-inline'
import { EventChanges } from './event-changes'
import { Tooltip, Position } from '@blueprintjs/core'

type ChangeEntry = {
  path: string
  relativePath: string[]
  value: any
}

interface IAsyncSilentEditItemProps extends IBaseProps {
  event: any
  owner: Owner
  index: number
  isFirst: boolean
  isLast: boolean
  workflowEntity: any
}

export const AsyncSilentEditItem: React.FC<IAsyncSilentEditItemProps> = (props) => {
  const { className, event, isFirst, owner, workflowEntity } = props
  const { createdBy, creationDate, processedDate, details = {} } = event || {}
  const { showInternals } = useContext(InternalViewContext)

  const entityPath = details.entityPath
  const entityValue = workflowEntity?.get?.(entityPath) ?? _.get(workflowEntity, entityPath)
  const initialPath = useMemo(() => normalizeEntityPath(entityPath), [entityPath])
  const changeEntries = useMemo(
    () => flattenEditValues(details.editCurrentEntityValues, initialPath, initialPath.length),
    [details.editCurrentEntityValues, initialPath]
  )
  const basePathLabel = useMemo(() => {
    if (!initialPath.length) {
      return undefined
    }
    return findSchemaLabel(workflowEntity, initialPath.join('.'))
  }, [workflowEntity, initialPath])

  const changeElements = changeEntries.map(({ path, relativePath, value }, idx) => {
    const schemaLabel = findSchemaLabel(workflowEntity, path)
    const label =
      schemaLabel && schemaLabel !== basePathLabel
        ? schemaLabel
        : formatRelativePathLabel(relativePath) || schemaLabel || path
    return (
      <div key={`${path}-${idx}`}>
        Updated <strong>{label}</strong> to {renderValue(value)}
      </div>
    )
  })

  return (
    <div className={classNames('c-timelineEvent', className)} key={event?.id}>
      <div
        className={classNames('c-timelineEvent-line', className, {
          'c-timelineEvent-line--topCutOff': isFirst,
        })}
      />
      <div className="c-timelineEvent-dot" />
      <div className="c-timelineEvent-contentContainer">
        <div className="c-timelineEvent-header">
          <div className="c-timelineEvent-author">
            <Avatar
              className='c-timelineEvent-avatar'
              name={createdBy?.displayName || 'System User'}
              size='xs'
            />
            <Tooltip
              disabled={!event.deviceInfo}
              content={renderDeviceInfoTooltipContent(event.deviceInfo)}
              position={Position.TOP}
            >
              <Label className="c-timelineEvent-authorName">
                {renderAuthorHeader(createdBy, owner?.firm)}
              </Label>
            </Tooltip>
          </div>
          <HelpBlock className="c-timelineEvent-timestamp">
            {renderEventTimestamp(creationDate, processedDate, showInternals)}
          </HelpBlock>
        </div>
        <div className="c-timelineEvent-body">
          <p className="mb1">
            Server updated{' '}
            {entityValue && entityValue.entityId ? (
              <EntityPreviewInline entity={entityValue} />
            ) : (
              <code>{entityPath || 'unknown entity'}</code>
            )}
          </p>
          <EventChanges>{changeElements}</EventChanges>
        </div>
      </div>
    </div>
  )
}

function flattenEditValues(value: any, prefix: string[] = [], baseLength = 0): ChangeEntry[] {
  if (_.isNil(value)) {
    return []
  }

  if (_.isArray(value)) {
    return _.flatMap(value, (item, index) =>
      flattenEditValues(item, prefix.concat([`[${index}]`]), baseLength)
    )
  }

  if (_.isPlainObject(value)) {
    if (isEntityRef(value) || isDateWrapper(value)) {
      return [
        {
          path: prefix.join('.'),
          relativePath: prefix.slice(baseLength),
          value,
        },
      ]
    }

    return _.flatMap(Object.entries(value), ([key, nested]) =>
      flattenEditValues(nested, prefix.concat([key]), baseLength)
    )
  }

  return [
    {
      path: prefix.join('.'),
      relativePath: prefix.slice(baseLength),
      value,
    },
  ]
}

function renderValue(value: any) {
  if (isEntityRef(value)) {
    return <EntityPreviewInline entity={value} />
  }

  if (isDateWrapper(value)) {
    return (
      <strong>{formatFeedDateTime(value.dateTime)}</strong>
    )
  }

  if (_.isPlainObject(value)) {
    return <code>{JSON.stringify(value)}</code>
  }

  if (_.isArray(value)) {
    return <code>{JSON.stringify(value)}</code>
  }

  if (_.isNil(value)) {
    return <em>unchanged</em>
  }

  return <strong>{String(value)}</strong>
}
